<script setup>
import { onMounted, computed } from "vue";
import { useSignupWizardStore } from "@/store";

import ReferalCodeVue from "./ReferalCodeVue.vue";
import MarketVue from "./MarketVue.vue";
import SelectMarket from "./SelectMarket.vue";
import MemberDetails from "./MemberDetails.vue";
import OrganisationInformation from "./OrganisationInformationVue.vue";
import OTPVerification from "./VerificationCodeVue.vue";
import CompleteRegistration from "./CompleteRegistrationVue.vue";
import PlanSelection from "./PlanSelection.vue";
import PasswordSetup from "./PasswordSetupVue.vue";
import SignupCompleted from "./SignupCompleted.vue";

import {ChevronLeft} from "lucide-vue-next"
import { storeToRefs } from "pinia";

const signupWizardStore = useSignupWizardStore();

const {currentStep} = storeToRefs(signupWizardStore);
const {fetchSignupState, goBack, isStandardPlan} = signupWizardStore;

onMounted(async () => {
    const storedStep = parseInt(localStorage.getItem("currentStep"));
    if (storedStep) currentStep.value = storedStep;

    await fetchSignupState();
});

const pageTitle = computed(() => {
  switch (currentStep.value) {
    case 0:
      return "Referral Code";
    case 1:
      return "Get Started";
    case 2:
      return "Select Market";
    case 3:
      return "User Information";
    case 4:
      return "OTP Confirmation";
    case 5:
      return "Subscription Selection";
    case 6:
      return isStandardPlan.value ? "Organization Setup" : "Market Setup";
    case 9:
      return "Create Password";
    default:
      return "";
  }
})

const showBackButton = computed(() => {
  const hideOn = [5,8];
  return currentStep.value > 1 && !hideOn.includes(currentStep.value);
})
</script>

<template>

  <div class="flex flex-col w-full overflow-x-hidden">
        <!-- Navigation Bar -->
        <nav class="bg-white w-full z-50">
            <div
                class="mx-auto px-4 py-6 flex items-center"
                :class="{
                    'gap-2 lg:gap-4': showBackButton,
                }"
            >
                <div>
                    <a href="/">
                        <img src="/logo/logo.png" alt="Amana Logo" class="h-[40px] w-[38px]" />
                    </a>
                </div>
                <div class="flex flex-1 items-center justify-between">
                  <div class="flex flex-1 items-center justify-center">
                      <span class="font-medium text-sm lg:text-xl text-primary-font">
                        {{ pageTitle }}
                      </span>
                  </div>
                  <div
                      v-if="showBackButton"
                      class="flex items-center justify-end cursor-pointer font-medium text-sm text-sub600"
                      @click="goBack"
                  >
                    <ChevronLeft class="w-4 h-4 mr-2" />
                    <span>Go Back</span>
                  </div>
                </div>
            </div>
            <div class="w-full h-px bg-primary-border"></div>
        </nav>

        <div class="w-full flex justify-center items-center">
          <template v-if="currentStep === 0">
              <ReferalCodeVue />
          </template>
          <template v-if="currentStep === 1">
              <MarketVue />
          </template>
          <template v-if="currentStep === 2">
              <SelectMarket />
          </template>
          <template v-if="currentStep === 3">
              <MemberDetails />
          </template>
          <template v-if="currentStep === 4">
              <OTPVerification />
          </template>
          <template v-if="currentStep === 5">
              <PlanSelection />
          </template>
          <template v-if="currentStep === 6">
              <OrganisationInformation />
          </template>
          <template v-if="currentStep === 7">
              <CompleteRegistration />
          </template>
          <template v-if="currentStep === 8">
              <SignupCompleted />
          </template>
          <template v-if="currentStep === 9">
              <PasswordSetup />
          </template>
        </div>
  </div>

    <div>
        <notificationGroup>
      <div
        class="fixed inset-0 flex px-4 py-6 pointer-events-none p-6 items-start justify-end z-9999"
      >
        <div class="max-w-sm w-full">
          <notification v-slot="{ notifications }">
            <div
              class="flex max-w-sm w-full mx-auto bg-white shadow-md rounded-lg overflow-hidden mt-4"
              v-for="notification in notifications"
              :key="notification.id"
            >
              <div class="flex justify-center items-center w-12 " :class="{
                        'bg-green-500 font-semibold': notification.title === 'Success',
                        'bg-red-500 font-semibold': notification.title !== 'Success'
                    }">
                <svg
                  class="h-6 w-6 fill-current text-white"
                  viewBox="0 0 40 40"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M20 3.33331C10.8 3.33331 3.33337 10.8 3.33337 20C3.33337 29.2 10.8 36.6666 20 36.6666C29.2 36.6666 36.6667 29.2 36.6667 20C36.6667 10.8 29.2 3.33331 20 3.33331ZM16.6667 28.3333L8.33337 20L10.6834 17.65L16.6667 23.6166L29.3167 10.9666L31.6667 13.3333L16.6667 28.3333Z"
                  ></path>
                </svg>
              </div>

              <div class="-mx-3 py-2 px-4">
                <div class="mx-3">
                    <span
                    :class="{
                        'text-green-500 font-semibold': notification.title === 'Success',
                        'text-red-500 font-semibold': notification.title !== 'Success'
                    }"
                    >
                    {{ notification.title }}
                    </span>
                    <p class="text-gray-600 text-sm">{{ notification.text }}</p>
                </div>
                </div>
            </div>
          </notification>
        </div>
      </div>
    </notificationGroup>
    </div>

</template>
